import { Link } from 'react-router-dom'
import { Seo } from '../components/Seo'
import { PageHeader } from '../components/common/PageHeader'
import { Reveal } from '../components/animation/Reveal'
import { Icon } from '../components/common/Icon'
import { useProducts } from '../hooks'
import { categoryBySlug } from '../lib/categories'
import { breadcrumbSchema } from '../lib/seo'

const GUIDES = [
  {
    icon: 'Sprout',
    title: 'Applying soil bio-fertilizers',
    body: 'For AGN, AGN LTE and BIODYNAMIC: mix liquids 1:40 with water and incubate at least one hour before soil application; apply by drench, flood or drip. Keep a 48-hour gap from any fungicide, pesticide or weedicide.',
  },
  {
    icon: 'Leaf',
    title: 'Foliar spraying best practice',
    body: 'For C4L Biofoliar, Nature’s Green 4+, Plant Armor Elite, AminOPF and Fulvic 25: spray in the early morning or evening, cover all foliage evenly, and — where noted — apply the mix within a few hours of preparation.',
  },
  {
    icon: 'Droplets',
    title: 'Fertigation, drenching & water management',
    body: 'AminOPF and Fulvic 25 apply at ~1 L/acre through fertigation or drenching. Hydretain is a concentrate — dilute at least 1:50 and water in thoroughly so it reaches the root zone.',
  },
  {
    icon: 'Package',
    title: 'Storage & shelf life',
    body: 'Store all products dry, frost-free and away from direct sunlight. Shelf life is three years for the AGN family and BIODYNAMIC, and five years for most other products.',
  },
]

export default function ResourcesPage() {
  const { data: products = [] } = useProducts()
  return (
    <>
      <Seo
        title="Usage & Application Guides | GreenbayBio"
        description="Practical guides for applying GreenbayBio products — soil bio-fertilizers, foliar sprays, fertigation and storage — plus a quick usage reference for every product."
        path="/resources"
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Resources', path: '/resources' }])]}
      />
      <PageHeader
        eyebrow="Usage & guides"
        title="How to get the best from every product."
        subtitle="Practical application guidance drawn straight from our product information — always follow the specific label and product page for your product."
      />

      <div className="container-x py-14">
        <Reveal className="grid gap-5 md:grid-cols-2" stagger={0.1} y={24}>
          {GUIDES.map((g) => (
            <div key={g.title} className="rounded-xl border border-sand-200 bg-white p-7 shadow-soft">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                <Icon name={g.icon} className="h-6 w-6" />
              </span>
              <h2 className="mt-5 font-display text-xl font-semibold text-ink">{g.title}</h2>
              <p className="mt-2 leading-relaxed text-ink-500">{g.body}</p>
            </div>
          ))}
        </Reveal>

        {/* quick reference */}
        <h2 className="mb-6 mt-14 font-display text-h2 font-semibold text-ink">Quick usage reference</h2>
        <div className="overflow-hidden rounded-xl border border-sand-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-sand-100/70 text-left">
                <th scope="col" className="px-5 py-3 font-semibold text-ink-700">Product</th>
                <th scope="col" className="px-5 py-3 font-semibold text-ink-700">Category</th>
                <th scope="col" className="px-5 py-3 font-semibold text-ink-700">Application</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.slug} className="border-t border-sand-200 align-top odd:bg-white even:bg-canvas/50">
                  <th scope="row" className="px-5 py-4 text-left font-medium">
                    <Link to={`/catalog/${p.slug}`} className="text-brand-700 hover:underline">
                      {p.name}
                    </Link>
                    {p.tagline && <span className="mt-0.5 block text-xs font-normal text-ink-400">{p.tagline}</span>}
                  </th>
                  <td className="px-5 py-4 text-ink-500">{categoryBySlug(p.categorySlug)?.name ?? '—'}</td>
                  <td className="px-5 py-4 text-ink-500">{p.applicationTags.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-ink-500">
          This is general guidance. Always follow the dosage, timing and precautions on the specific product page and label.
        </p>
      </div>
    </>
  )
}
